import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/contexts/AuthContext';
import { 
  BarChart3, Plus, Vote, Trash2, Check, Clock, Lock, Users, Loader2, X
} from 'lucide-react';
import { getErrorMessage } from '../utils/errorHandler';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PollsTab = ({ communityId, isAdmin }) => {
  const { user } = useAuth();
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [voting, setVoting] = useState(null);
  
  // New poll form
  const [newPoll, setNewPoll] = useState({
    question: '',
    options: ['', ''],
    allow_multiple: false,
    anonymous: false,
    closes_in_hours: null
  });

  const fetchPolls = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/communities/${communityId}/polls?include_closed=true`,
        { withCredentials: true }
      );
      setPolls(response.data);
    } catch (error) {
      console.error('Failed to fetch polls:', error);
    } finally {
      setLoading(false);
    }
  }, [communityId]);

  useEffect(() => {
    fetchPolls();
  }, [fetchPolls]);

  const handleCreatePoll = async () => {
    if (!newPoll.question.trim()) {
      toast.error('Please enter a question');
      return;
    }
    
    const validOptions = newPoll.options.filter(o => o.trim());
    if (validOptions.length < 2) {
      toast.error('Please add at least 2 options');
      return;
    }
    
    try {
      await axios.post(
        `${API}/communities/${communityId}/polls`,
        {
          question: newPoll.question,
          options: validOptions,
          allow_multiple: newPoll.allow_multiple,
          anonymous: newPoll.anonymous,
          closes_in_hours: newPoll.closes_in_hours
        },
        { withCredentials: true }
      );
      
      toast.success('Poll created!');
      setCreateDialogOpen(false);
      setNewPoll({
        question: '',
        options: ['', ''],
        allow_multiple: false,
        anonymous: false,
        closes_in_hours: null
      });
      fetchPolls();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create poll'));
    }
  };

  const handleVote = async (pollId, optionIds) => {
    setVoting(pollId);
    try {
      await axios.post(
        `${API}/communities/${communityId}/polls/${pollId}/vote`,
        { option_ids: optionIds },
        { withCredentials: true }
      );
      toast.success('Vote recorded!');
      fetchPolls();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to vote'));
    } finally {
      setVoting(null);
    }
  };

  const handleClosePoll = async (pollId) => {
    try {
      await axios.post(
        `${API}/communities/${communityId}/polls/${pollId}/close`,
        {},
        { withCredentials: true }
      );
      toast.success('Poll closed');
      fetchPolls();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to close poll'));
    }
  };

  const addOption = () => {
    if (newPoll.options.length < 10) {
      setNewPoll({ ...newPoll, options: [...newPoll.options, ''] });
    }
  };

  const removeOption = (index) => {
    if (newPoll.options.length > 2) {
      setNewPoll({
        ...newPoll,
        options: newPoll.options.filter((_, i) => i !== index)
      });
    }
  };

  const updateOption = (index, value) => {
    const updated = [...newPoll.options];
    updated[index] = value;
    setNewPoll({ ...newPoll, options: updated });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Create Button */}
      {isAdmin && (
        <div className="flex justify-end">
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="create-poll-btn">
                <Plus className="w-4 h-4" />
                Create Poll
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Create Poll</DialogTitle>
                <DialogDescription>Ask your community a question</DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div>
                  <Label>Question</Label>
                  <Input
                    placeholder="What would you like to ask?"
                    value={newPoll.question}
                    onChange={(e) => setNewPoll({ ...newPoll, question: e.target.value })}
                    className="mt-1"
                    data-testid="poll-question-input"
                  />
                </div>
                
                <div>
                  <Label>Options</Label>
                  <div className="space-y-2 mt-1">
                    {newPoll.options.map((opt, idx) => (
                      <div key={idx} className="flex gap-2">
                        <Input
                          placeholder={`Option ${idx + 1}`}
                          value={opt}
                          onChange={(e) => updateOption(idx, e.target.value)}
                          data-testid={`poll-option-${idx}`}
                        />
                        {newPoll.options.length > 2 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeOption(idx)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    {newPoll.options.length < 10 && (
                      <Button variant="outline" size="sm" onClick={addOption} className="w-full">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Option
                      </Button>
                    )}
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Allow multiple selections</Label>
                    <Switch
                      checked={newPoll.allow_multiple}
                      onCheckedChange={(checked) => setNewPoll({ ...newPoll, allow_multiple: checked })}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label>Anonymous voting</Label>
                    <Switch
                      checked={newPoll.anonymous}
                      onCheckedChange={(checked) => setNewPoll({ ...newPoll, anonymous: checked })}
                    />
                  </div>
                  
                  <div>
                    <Label>Auto-close after (hours)</Label>
                    <Input
                      type="number"
                      placeholder="No auto-close"
                      value={newPoll.closes_in_hours || ''}
                      onChange={(e) => setNewPoll({ 
                        ...newPoll, 
                        closes_in_hours: e.target.value ? parseInt(e.target.value) : null 
                      })}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleCreatePoll} data-testid="submit-poll-btn">Create Poll</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Polls List */}
      {polls.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="font-medium">No polls yet</p>
          <p className="text-sm">Create a poll to gather opinions from your community</p>
        </div>
      ) : (
        <ScrollArea className="h-[400px] pr-4">
          <div className="space-y-4">
            <AnimatePresence>
              {polls.map((poll) => (
                <PollCard
                  key={poll.poll_id}
                  poll={poll}
                  userId={user?.user_id}
                  isAdmin={isAdmin}
                  voting={voting === poll.poll_id}
                  onVote={(optionIds) => handleVote(poll.poll_id, optionIds)}
                  onClose={() => handleClosePoll(poll.poll_id)}
                />
              ))}
            </AnimatePresence>
          </div>
        </ScrollArea>
      )}
    </div>
  );
};

const PollCard = ({ poll, userId, isAdmin, voting, onVote, onClose }) => {
  const [selectedOptions, setSelectedOptions] = useState(poll.user_votes || []);
  const hasVoted = poll.user_votes?.length > 0;
  const canVote = !poll.is_closed && !hasVoted;
  
  const toggleOption = (optionId) => {
    if (poll.allow_multiple) {
      setSelectedOptions(prev => 
        prev.includes(optionId) 
          ? prev.filter(id => id !== optionId)
          : [...prev, optionId]
      );
    } else {
      setSelectedOptions([optionId]);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
    >
      <Card className={poll.is_closed ? 'opacity-75' : ''}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-base">{poll.question}</CardTitle>
              <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                <span>by {poll.creator_name}</span>
                <span>•</span>
                <span>{poll.total_votes} vote{poll.total_votes !== 1 ? 's' : ''}</span>
                {poll.anonymous && (
                  <>
                    <span>•</span>
                    <Badge variant="outline" className="text-xs">
                      <Lock className="w-3 h-3 mr-1" />
                      Anonymous
                    </Badge>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {poll.is_closed ? (
                <Badge variant="secondary">Closed</Badge>
              ) : poll.closes_at && (
                <Badge variant="outline" className="text-xs">
                  <Clock className="w-3 h-3 mr-1" />
                  Auto-closes
                </Badge>
              )}
              {isAdmin && !poll.is_closed && (
                <Button variant="ghost" size="sm" onClick={onClose}>
                  <Lock className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {poll.options.map((opt) => {
              const percentage = poll.total_votes > 0 
                ? Math.round((opt.votes / poll.total_votes) * 100) 
                : 0;
              const isSelected = selectedOptions.includes(opt.option_id);
              const wasVoted = poll.user_votes?.includes(opt.option_id);
              
              return (
                <div key={opt.option_id} className="space-y-1">
                  <div 
                    className={`flex items-center gap-3 p-2 rounded-lg border transition-colors ${
                      canVote 
                        ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800' 
                        : ''
                    } ${isSelected ? 'border-primary bg-primary/5' : 'border-slate-200 dark:border-slate-700'}`}
                    onClick={() => canVote && toggleOption(opt.option_id)}
                  >
                    {canVote ? (
                      poll.allow_multiple ? (
                        <Checkbox checked={isSelected} />
                      ) : (
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          isSelected ? 'border-primary' : 'border-slate-300'
                        }`}>
                          {isSelected && <div className="w-2 h-2 rounded-full bg-primary" />}
                        </div>
                      )
                    ) : wasVoted ? (
                      <Check className="w-4 h-4 text-primary" />
                    ) : (
                      <div className="w-4 h-4" />
                    )}
                    <span className="flex-1 text-sm">{opt.text}</span>
                    <span className="text-sm font-medium">{percentage}%</span>
                  </div>
                  <Progress value={percentage} className="h-1" />
                </div>
              );
            })}
          </div>
          
          {canVote && selectedOptions.length > 0 && (
            <Button 
              className="w-full mt-4" 
              onClick={() => onVote(selectedOptions)}
              disabled={voting}
              data-testid={`vote-btn-${poll.poll_id}`}
            >
              {voting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Vote className="w-4 h-4 mr-2" />
              )}
              Vote
            </Button>
          )}
          
          {hasVoted && !poll.is_closed && (
            <p className="text-xs text-center text-muted-foreground mt-3">
              You voted for this poll
            </p>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default PollsTab;
